import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  FileText, Upload, Camera, Shield, Download, Search, Calculator,
  Check, ChevronDown, ChevronUp, ArrowRight, Lock, Sparkles, Mail
} from "lucide-react";

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-200 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        style={{ display: "flex" }} className="w-full flex items-center justify-between py-4 text-left"
      >
        <span className="font-medium text-gray-900 pr-4">{question}</span>
        {open ? <ChevronUp className="h-5 w-5 text-gray-400 flex-shrink-0" /> : <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />}
      </button>
      {open && <p className="text-gray-600 pb-4 text-sm leading-relaxed">{answer}</p>}
    </div>
  );
}

export default function BelegerfassungSEO() {
  const navigate = useNavigate();

  useSEO({
    title: "Belegerfassung für Freelancer – KI-Erkennung | ivoliq",
    description: "Belege per Drag & Drop, Foto oder E-Mail-Weiterleitung erfassen. KI erkennt Betrag, Händler, MwSt automatisch. AfA-Vorschlag nach DACH-Recht. DSGVO-konform. ZIP-Export für den Steuerberater.",
  });

  const features = [
    {
      icon: Upload,
      title: "Drag & Drop Upload",
      desc: "Ziehe PDF-Rechnungen oder Fotos direkt in ivoliq. Unterstützt PDF, JPG, PNG, HEIC und WEBP.",
    },
    {
      icon: Camera,
      title: "Kamera-Upload",
      desc: "Unterwegs einen Beleg erhalten? Fotografiere ihn direkt mit dem Handy und lade ihn hoch.",
    },
    {
      icon: Mail,
      title: "Per E-Mail einreichen (Pro)",
      desc: "Leite Rechnungen an deine persönliche Beleg-Adresse weiter – sie erscheinen automatisch erfasst in ivoliq.",
    },
    {
      icon: Sparkles,
      title: "KI-Erkennung (Pro)",
      desc: "Betrag, Händler, MwSt-Satz und Kategorie werden automatisch erkannt. DSGVO-konforme Verarbeitung.",
    },
    {
      icon: Calculator,
      title: "AfA-Vorschlag",
      desc: "Automatische Prüfung der GWG-Grenzen nach DE/AT/CH-Recht. Abschreibungsplan mit einem Klick.",
    },
    {
      icon: Search,
      title: "Suche & Filter",
      desc: "Finde jeden Beleg sofort – nach Händler, Betrag, Kategorie oder Datum.",
    },
    {
      icon: Download,
      title: "ZIP + Excel Export",
      desc: "Alle Belege mit einheitlichem Naming plus Excel-Übersicht. Perfekt für den Steuerberater.",
    },
    {
      icon: Shield,
      title: "DSGVO-konform",
      desc: "Belege werden in deutschen Rechenzentren gespeichert. KI-Verarbeitung nur mit Einwilligung.",
    },
    {
      icon: Lock,
      title: "Verschlüsselt",
      desc: "Alle Übertragungen per TLS. Serverseitige Verschlüsselung im Hetzner Object Storage.",
    },
  ];

  const comparisonData = [
    { feature: "Beleg-Upload (PDF + Bilder)", free: true, basic: true, pro: true },
    { feature: "Lokale OCR-Erkennung", free: false, basic: true, pro: true },
    { feature: "KI-Belegerkennung", free: false, basic: false, pro: true },
    { feature: "Beleg-Eingang per E-Mail", free: false, basic: false, pro: true },
    { feature: "13 Standardkategorien", free: true, basic: true, pro: true },
    { feature: "Eigene Kategorien", free: false, basic: true, pro: true },
    { feature: "AfA-Vorschlag (DACH)", free: true, basic: true, pro: true },
    { feature: "Duplikaterkennung", free: true, basic: true, pro: true },
    { feature: "ZIP + Excel Export", free: false, basic: true, pro: true },
    { feature: "Beleg-Reminder", free: true, basic: true, pro: true },
  ];

  const faqItems = [
    {
      q: "Welche Dateiformate werden unterstützt?",
      a: "Du kannst PDF-Dateien, JPG, JPEG, PNG, HEIC (iPhone-Fotos), HEIF und WEBP hochladen. Maximale Dateigröße: 25 MB. Hochauflösende Fotos werden automatisch komprimiert.",
    },
    {
      q: "Wie funktioniert die KI-Belegerkennung?",
      a: "Die KI analysiert dein Belegbild und erkennt automatisch Betrag, Händler, MwSt-Satz, Datum und Kategorie. Die Erkennung ist im Pro-Plan enthalten und nutzt modernste Vision-KI (GPT-4o / Claude). Die Datenverarbeitung erfolgt nur mit deiner ausdrücklichen DSGVO-Einwilligung.",
    },
    {
      q: "Ist die Belegerfassung DSGVO-konform?",
      a: "Ja. Belege werden ausschließlich auf deutschen Servern (Hetzner, Nürnberg) gespeichert. Die KI-Erkennung ist optional und erfordert eine separate Einwilligung nach Art. 6 Abs. 1 lit. a DSGVO. Ohne KI-Einwilligung wird die kostenlose lokale OCR-Erkennung verwendet.",
    },
    {
      q: "Was ist der AfA-Vorschlag?",
      a: "Bei jeder Ausgabe prüft ivoliq automatisch, ob eine Sofortabschreibung möglich ist (DE: bis 800€ netto, AT: bis 1.000€ netto) oder ob ein Abschreibungsplan (AfA) erstellt werden muss. Du bekommst einen konkreten Vorschlag inkl. Abschreibungstabelle.",
    },
    {
      q: "Kann ich die Belege für meinen Steuerberater exportieren?",
      a: "Ja. Der ZIP-Export enthält alle Belegdateien mit einheitlichem Naming (Datum_Händler_Betrag) plus eine Excel-Übersicht mit allen extrahierten Daten. Der Zeitraum ist frei wählbar (Quartal, Jahr oder individuell).",
    },
    {
      q: "Werden Duplikate erkannt?",
      a: "Ja. Wenn du einen Beleg hochlädst, der denselben Betrag und ein ähnliches Datum wie eine bestehende Ausgabe hat, warnt dich ivoliq vor dem möglichen Duplikat.",
    },
    {
      q: "Kann ich Belege per E-Mail einreichen?",
      a: "Ja. Im Pro-Plan bekommst du eine persönliche Beleg-E-Mail-Adresse (z. B. max-a1b2@belege.ivoliq.io). Rechnungen, die du dorthin sendest oder weiterleitest, werden automatisch erfasst: Die KI liest Betrag, Händler, MwSt und Kategorie aus, prüft auf Duplikate und legt den Beleg zur Bestätigung in deinen Posteingang. Auch Rechnungen direkt vom Lieferanten an diese Adresse funktionieren.",
    },
    {
      q: "Ist die Beleg-E-Mail-Adresse sicher?",
      a: "Ja. Die Adresse enthält einen zufälligen Sicherheits-Code und ist dadurch nicht erratbar. Mails an unbekannte Adressen werden verworfen, automatische Antworten und Newsletter werden gefiltert, und jeder eingegangene Beleg wartet auf deine manuelle Bestätigung, bevor er in Auswertungen einfließt. Bei Bedarf kannst du jederzeit eine neue Adresse erzeugen.",
    },
    {
      q: "Funktioniert die Belegerfassung auch auf dem Handy?",
      a: "Ja. Du kannst Belege direkt mit der Handy-Kamera fotografieren und hochladen. Die Oberfläche ist vollständig mobiloptimiert.",
    },
    {
      q: "Was kostet die Belegerfassung?",
      a: "Die Grundfunktionen (Upload, Kategorien, AfA-Vorschlag, Duplikaterkennung) sind kostenlos. Die OCR-Erkennung ist im Basic-Plan enthalten, die erweiterte KI-Erkennung im Pro-Plan.",
    },
  ];

  // FAQ Schema.org markup
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold text-gray-900">ivoliq</Link>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm text-gray-600 hover:text-gray-900">Anmelden</Link>
            <Button size="sm" onClick={() => navigate("/register")} className="bg-emerald-600 hover:bg-emerald-700">
              Kostenlos starten
            </Button>
          </div>
        </div>
      </header>

      {/* Schema.org FAQ */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      {/* Hero */}
      <section className="pt-24 pb-16 bg-gradient-to-b from-emerald-50 to-white">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
            <FileText className="h-4 w-4" /> Belegerfassung für Freelancer
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
            Belege digital erfassen.<br />
            <span className="text-emerald-600">KI liest alles aus.</span>
          </h1>
          <p className="text-lg text-gray-600 mt-6 max-w-2xl mx-auto leading-relaxed">
            Fotografiere oder ziehe deine Belege per Drag & Drop in ivoliq.
            Betrag, Händler und MwSt werden automatisch erkannt – DSGVO-konform und steuerlich korrekt nach DACH-Recht.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
            <Button
              size="lg"
              onClick={() => navigate("/register")}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-8"
              data-testid="seo-beleg-cta-register"
            >
              Kostenlos starten <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })}
            >
              Funktionen ansehen
            </Button>
          </div>
        </div>
      </section>

      {/* Pain Point */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900">
            Schluss mit dem Beleg-Chaos zum Jahresende
          </h2>
          <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
            Kassenzettel im Schuhkarton, fehlende Belege bei der Steuererklärung, stundenlanges Sortieren für den Steuerberater – das muss nicht sein.
            Mit ivoliq erfasst du jeden Beleg sofort digital und hast alles jederzeit griffbereit.
          </p>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 text-center mb-12">
            Alles was du für deine Belege brauchst
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f) => (
              <Card key={f.title} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="pt-6">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center mb-3">
                    <f.icon className="h-5 w-5 text-emerald-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">{f.title}</h3>
                  <p className="text-sm text-gray-600">{f.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 text-center mb-12">
            So funktioniert's – in 3 Schritten
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "1", title: "Beleg hochladen", desc: "Ziehe deinen Beleg per Drag & Drop rein, fotografiere ihn – oder leite die Rechnung per E-Mail an deine persönliche Beleg-Adresse weiter." },
              { step: "2", title: "Daten prüfen", desc: "Die KI erkennt Betrag, Händler, MwSt und Kategorie. Du prüfst und ergänzt." },
              { step: "3", title: "Exportieren", desc: "ZIP mit allen Belegen + Excel-Übersicht für deinen Steuerberater herunterladen." },
            ].map((s) => (
              <div key={s.step} className="text-center">
                <div className="w-12 h-12 rounded-full bg-emerald-600 text-white flex items-center justify-center mx-auto text-xl font-bold mb-4">
                  {s.step}
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{s.title}</h3>
                <p className="text-sm text-gray-600">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NEU: Belege per E-Mail einreichen */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="rounded-2xl border border-sky-200 bg-sky-50/60 p-8 sm:p-10">
            <div className="inline-flex items-center gap-2 bg-sky-600 text-white px-3 py-1 rounded-full text-xs font-semibold mb-4">
              <Mail className="h-3.5 w-3.5" /> NEU
            </div>
            <h2 className="text-base sm:text-lg font-bold text-gray-900">
              Belege per E-Mail einreichen – die persönliche Beleg-Adresse
            </h2>
            <p className="text-gray-600 mt-3 leading-relaxed">
              Viele Rechnungen kommen ohnehin per E-Mail: Hosting, Software-Abos, Telefon, Online-Bestellungen.
              Mit ivoliq Pro bekommst du eine persönliche Beleg-E-Mail-Adresse
              (z.&nbsp;B. <span className="font-mono text-sm bg-white border rounded px-1.5 py-0.5">max-a1b2@belege.ivoliq.io</span>).
              Leite Rechnungen einfach dorthin weiter – oder hinterlege die Adresse direkt beim Lieferanten.
            </p>
            <ul className="mt-5 space-y-2">
              <li className="flex items-start gap-2 text-gray-700 text-sm">
                <Check className="h-4 w-4 text-sky-600 flex-shrink-0 mt-0.5" />
                <span>PDF- und Bild-Anhänge werden automatisch erfasst – die KI liest Betrag, Händler, MwSt und Kategorie aus</span>
              </li>
              <li className="flex items-start gap-2 text-gray-700 text-sm">
                <Check className="h-4 w-4 text-sky-600 flex-shrink-0 mt-0.5" />
                <span>Duplikaterkennung inklusive: doppelt weitergeleitete Rechnungen werden erkannt</span>
              </li>
              <li className="flex items-start gap-2 text-gray-700 text-sm">
                <Check className="h-4 w-4 text-sky-600 flex-shrink-0 mt-0.5" />
                <span>Jeder Eingang wartet im Posteingang auf deine Bestätigung – du behältst die Kontrolle</span>
              </li>
              <li className="flex items-start gap-2 text-gray-700 text-sm">
                <Check className="h-4 w-4 text-sky-600 flex-shrink-0 mt-0.5" />
                <span>Nicht erratbare Adresse mit Sicherheits-Code, jederzeit neu generierbar</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 text-center mb-8">
            Belegerfassung im Planvergleich
          </h2>
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Funktion</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Free</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Basic</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-emerald-700 bg-emerald-50">Pro</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="px-4 py-3 text-sm text-gray-700">{row.feature}</td>
                    <td className="text-center px-4 py-3">
                      {row.free ? <Check className="h-4 w-4 text-emerald-600 mx-auto" /> : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="text-center px-4 py-3">
                      {row.basic ? <Check className="h-4 w-4 text-emerald-600 mx-auto" /> : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="text-center px-4 py-3 bg-emerald-50/50">
                      {row.pro ? <Check className="h-4 w-4 text-emerald-600 mx-auto" /> : <span className="text-gray-300">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-center mt-6">
            <Button
              onClick={() => navigate("/register")}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="seo-beleg-cta-pricing"
            >
              Jetzt kostenlos starten
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 text-center mb-8">
            Häufige Fragen zur Belegerfassung
          </h2>
          <div className="bg-white rounded-xl border shadow-sm p-6" data-testid="seo-beleg-faq">
            {faqItems.map((item, i) => (
              <FAQItem key={i} question={item.q} answer={item.a} />
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-emerald-600">
        <div className="max-w-3xl mx-auto px-4 text-center text-white">
          <h2 className="text-base sm:text-lg font-bold mb-4">
            Starte jetzt mit der digitalen Belegerfassung
          </h2>
          <p className="text-emerald-100 mb-8">
            Kostenlos registrieren, Belege hochladen, Steuerberater beeindrucken.
          </p>
          <Button
            size="lg"
            onClick={() => navigate("/register")}
            className="bg-white text-emerald-700 hover:bg-emerald-50 px-8"
            data-testid="seo-beleg-cta-final"
          >
            Kostenlos starten <ArrowRight className="h-5 w-5 ml-2" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
